import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

function formatStepCompletedAt(iso: string | null | undefined): string | null {
  if (iso == null || iso === "") return null;
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

import {
  completeTransporterOnboarding,
  deriveTransporterOnboardingStep,
  failTransporterOnboarding,
  fetchTransporterOnboarding,
  TRANSPORTER_ONBOARDING_FLOW,
  type OnboardingRecordDto,
  type TransporterOnboardingStep,
} from "../lib/fetch-transporter-onboarding";
import {
  fetchTransporterProfile,
  patchTransporterKybProfile,
  type TransporterKybPatchBody,
  type TransporterProfileDto,
} from "../lib/fetch-transporter-delivery-ops";
import {
  createTransporterBankLinkToken,
  exchangeTransporterBankPublicToken,
  formatTransporterBankLinkExchangeError,
  formatTransporterBankLinkStepError,
} from "../lib/transporter-plaid-bank-client";
import { TransporterPlaidLinkButton } from "../components/TransporterPlaidLinkButton";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

type KybFormState = {
  legalName: string;
  dbaName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  medLicenseNumber: string;
  medLicenseExpiresAt: string;
  hasDeliveryPermit: boolean;
  deliveryPermitNumber: string;
  deliveryPermitExpiresAt: string;
};

function isoDateInput(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

function profileToKybForm(p: TransporterProfileDto): KybFormState {
  return {
    legalName: p.legalName,
    dbaName: p.dbaName ?? "",
    addressLine1: p.addressLine1,
    addressLine2: p.addressLine2 ?? "",
    city: p.city,
    state: p.state,
    zip: p.zip,
    phone: p.phone,
    email: p.email,
    medLicenseNumber: p.medLicenseNumber,
    medLicenseExpiresAt: isoDateInput(p.medLicenseExpiresAt),
    hasDeliveryPermit: p.hasDeliveryPermit,
    deliveryPermitNumber: p.deliveryPermitNumber ?? "",
    deliveryPermitExpiresAt: p.deliveryPermitExpiresAt ? isoDateInput(p.deliveryPermitExpiresAt) : "",
  };
}

function kybFormToPatch(f: KybFormState): TransporterKybPatchBody {
  const zip = f.zip.replace(/\D/g, "").slice(0, 5);
  const permitExp =
    f.hasDeliveryPermit && f.deliveryPermitExpiresAt.trim().length >= 10
      ? f.deliveryPermitExpiresAt.trim().slice(0, 10)
      : null;
  return {
    legalName: f.legalName.trim(),
    dbaName: f.dbaName.trim() ? f.dbaName.trim() : null,
    addressLine1: f.addressLine1.trim(),
    addressLine2: f.addressLine2.trim() ? f.addressLine2.trim() : null,
    city: f.city.trim(),
    state: f.state.trim().toUpperCase().slice(0, 2),
    zip,
    phone: f.phone.trim(),
    email: f.email.trim(),
    medLicenseNumber: f.medLicenseNumber.trim(),
    medLicenseExpiresAt: f.medLicenseExpiresAt.trim().slice(0, 10),
    hasDeliveryPermit: f.hasDeliveryPermit,
    deliveryPermitNumber:
      f.hasDeliveryPermit && f.deliveryPermitNumber.trim() ? f.deliveryPermitNumber.trim() : null,
    deliveryPermitExpiresAt: f.hasDeliveryPermit ? permitExp : null,
  };
}

function kybFormValid(f: KybFormState): string | null {
  if (!f.legalName.trim()) return "Legal name is required.";
  if (!f.addressLine1.trim()) return "Address line 1 is required.";
  if (!f.city.trim()) return "City is required.";
  if (f.state.trim().length !== 2) return "State must be two letters.";
  if (!/^\d{5}$/.test(f.zip.replace(/\D/g, "").slice(0, 5))) return "ZIP must be five digits.";
  if (!f.phone.trim()) return "Phone is required.";
  if (!f.email.trim()) return "Email is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return "Enter a valid email address.";
  if (!f.medLicenseNumber.trim()) return "MED license number is required.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.medLicenseExpiresAt.trim().slice(0, 10)))
    return "MED license expiry must be a valid date (YYYY-MM-DD).";
  if (f.hasDeliveryPermit) {
    if (!f.deliveryPermitNumber.trim()) return "Delivery permit number is required when you have a delivery permit.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.deliveryPermitExpiresAt.trim().slice(0, 10)))
      return "Delivery permit expiry must be a valid date (YYYY-MM-DD).";
  }
  return null;
}

function MetrcComplianceCallout({ variant }: { variant: "kyb" | "fleet" | "driver" }) {
  const copy = {
    kyb:
      "Retail Metrc transfers are initiated by dispensaries. On runs, the platform ties deliveries to Metrc manifests; you do not enter a Metrc API key for your company here. Confirm your Colorado MED license and delivery permit below match what regulators expect.",
    fleet:
      "Vehicles in Fleet must meet Colorado MED checklist items (registration, insurance, alarm, storage, video). Once a run has a Metrc manifest, changing the assigned driver may require a void and dispensary coordination.",
    driver:
      "Drivers scan Metrc package tags at pickup. Every driver needs a valid MED badge and Colorado driver's license—manage roster and expirations under Account → Team and monitor shifts in Fleet.",
  } as const;
  return (
    <div className="td-surface px-6 py-4 text-sm text-td-body">
      <p className="font-medium text-td-ink">Metrc & compliance</p>
      <p className="mt-1.5 leading-relaxed">{copy[variant]}</p>
    </div>
  );
}

const STEP_LABEL: Record<TransporterOnboardingStep, string> = {
  kyb_verification: "Business verification (KYB)",
  bank_account_linking: "Bank & payouts",
  fee_schedule_agreement: "Fee schedule",
  vehicle_registration: "Fleet vehicles",
  driver_registration: "Drivers & credentials",
  completed: "Finish setup",
};

export function TransporterOnboardingScreen() {
  const navigate = useNavigate();
  const { transporterId, getToken, refreshWorkspaceProfile } = useTransporterWorkspace();
  const [profileName, setProfileName] = useState<string | null>(null);
  const [profileDone, setProfileDone] = useState<string | null>(null);
  const [records, setRecords] = useState<OnboardingRecordDto[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
  const [plaidTokenLoading, setPlaidTokenLoading] = useState(false);
  const [plaidTokenErr, setPlaidTokenErr] = useState<string | null>(null);
  const [plaidExchangeBusy, setPlaidExchangeBusy] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [failReason, setFailReason] = useState("");
  const [kybForm, setKybForm] = useState<KybFormState | null>(null);

  const load = useCallback(async () => {
    if (!transporterId) return;
    setLoadError(null);
    try {
      const [p, o] = await Promise.all([
        fetchTransporterProfile(getToken, transporterId),
        fetchTransporterOnboarding(getToken, transporterId),
      ]);
      setProfileName(p.dbaName?.trim() || p.legalName);
      setProfileDone(p.onboardingCompletedAt);
      setRecords(o.records);
      setKybForm(profileToKybForm(p));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setKybForm(null);
    }
  }, [transporterId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const derived = useMemo(
    () => deriveTransporterOnboardingStep(records, profileDone),
    [records, profileDone],
  );

  const serverStep = derived.done ? null : derived.step;
  const serverStepIdx = serverStep == null ? -1 : TRANSPORTER_ONBOARDING_FLOW.indexOf(serverStep);
  const [viewIdx, setViewIdx] = useState(0);

  useEffect(() => {
    if (serverStep == null) return;
    setViewIdx(TRANSPORTER_ONBOARDING_FLOW.indexOf(serverStep));
  }, [records, profileDone, serverStep]);

  useEffect(() => {
    if (!transporterId || !derived.done) return;
    navigate({ to: "/workspace/account", search: { tab: "org" }, replace: true });
  }, [transporterId, derived.done, navigate]);

  const step = !derived.done ? TRANSPORTER_ONBOARDING_FLOW[viewIdx] : null;
  const isReviewingDoneStep = serverStepIdx >= 0 && viewIdx < serverStepIdx;
  const canGoBack = viewIdx > 0;
  const canGoForward = serverStepIdx >= 0 && viewIdx < serverStepIdx;

  useEffect(() => {
    if (!transporterId || step !== "bank_account_linking" || isReviewingDoneStep) {
      setPlaidLinkToken(null);
      setPlaidTokenErr(null);
      setPlaidTokenLoading(false);
      return;
    }
    let cancelled = false;
    setPlaidTokenLoading(true);
    setPlaidTokenErr(null);
    void createTransporterBankLinkToken(getToken, transporterId)
      .then((r) => {
        if (!cancelled) setPlaidLinkToken(r.linkToken);
      })
      .catch((e) => {
        if (!cancelled) setPlaidTokenErr(formatTransporterBankLinkStepError(e));
      })
      .finally(() => {
        if (!cancelled) setPlaidTokenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transporterId, getToken, step, isReviewingDoneStep]);

  const onBankPlaidSuccess = async (publicToken: string, metadata: { accounts?: { id: string }[] }) => {
    if (!transporterId) return;
    const accountId = metadata.accounts?.[0]?.id;
    setPlaidExchangeBusy(true);
    setActionError(null);
    try {
      await exchangeTransporterBankPublicToken(getToken, transporterId, publicToken, accountId);
      await completeTransporterOnboarding(getToken, transporterId, {
        step: "bank_account_linking",
        metadata: { source: "transporter_web", bankLink: "plaid" },
      });
      await load();
      await refreshWorkspaceProfile();
    } catch (e) {
      setActionError(formatTransporterBankLinkExchangeError(e));
    } finally {
      setPlaidExchangeBusy(false);
    }
  };

  const onKybSaveAndContinue = async () => {
    if (!transporterId || !kybForm) return;
    const err = kybFormValid(kybForm);
    if (err) {
      setActionError(err);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await patchTransporterKybProfile(getToken, transporterId, kybFormToPatch(kybForm));
      await completeTransporterOnboarding(getToken, transporterId, {
        step: "kyb_verification",
        metadata: { source: "transporter_web" },
      });
      await load();
      await refreshWorkspaceProfile();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onComplete = async (step: TransporterOnboardingStep, extra?: Record<string, unknown>) => {
    if (!transporterId) return;
    setBusy(true);
    setActionError(null);
    try {
      const body: Parameters<typeof completeTransporterOnboarding>[2] = {
        step,
        metadata: { source: "transporter_web", ...extra },
      };
      await completeTransporterOnboarding(getToken, transporterId, body);
      await load();
      await refreshWorkspaceProfile();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onFail = async () => {
    if (!transporterId || serverStep == null || viewIdx !== serverStepIdx || !failReason.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await failTransporterOnboarding(getToken, transporterId, {
        step: serverStep,
        failureReason: failReason.trim(),
      });
      setFailReason("");
      setFailOpen(false);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!transporterId) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray">
          Select a transporter (platform admin) or use an account linked to a transporter to continue.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red">Could not load onboarding. Try again.</p>
        <button
          type="button"
          className="btn-secondary px-3"
          onClick={() => void load()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (derived.done) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray">Opening Account…</p>
      </div>
    );
  }

  if (kybForm === null) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray">Loading transporter profile…</p>
      </div>
    );
  }

  if (!step) {
    return null;
  }

  const failedReason = !derived.done ? derived.failedReason : null;
  const recordForView = records.find((r) => r.step === step);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        {profileName ? <p className="text-sm font-medium text-dark">{profileName}</p> : null}
      </div>

      <ol className="flex flex-wrap gap-2">
        {TRANSPORTER_ONBOARDING_FLOW.map((s, i) => {
          const done = records.some((r) => r.step === s && r.completedAt);
          const reachable = i <= serverStepIdx;
          const active = i === viewIdx;
          return (
            <li key={s}>
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && setViewIdx(i)}
                title={reachable ? `Go to ${STEP_LABEL[s]}` : "Complete earlier steps first"}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  done
                    ? "bg-green-pale text-green"
                    : active
                      ? "bg-green-pale text-green ring-1 ring-green/25"
                      : "bg-bg text-gray"
                } ${reachable && !active ? "cursor-pointer hover:bg-gray-100" : ""}`}
              >
                {i + 1}. {STEP_LABEL[s]}
              </button>
            </li>
          );
        })}
      </ol>

      {failedReason && viewIdx === serverStepIdx ? (
        <div className="rounded-2xl border border-amber-light bg-amber-bg px-6 py-4 text-sm text-text shadow-td-card">
          <p className="font-medium text-dark">Previous attempt</p>
          <p className="mt-1 text-gray">{failedReason}</p>
        </div>
      ) : null}

      <section className="td-surface p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <h2 className="text-lg font-semibold text-dark">{STEP_LABEL[step]}</h2>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={!canGoBack}
              onClick={() => setViewIdx((j) => j - 1)}
              className="btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Back
            </button>
            <button
              type="button"
              disabled={!canGoForward}
              onClick={() => canGoForward && setViewIdx((j) => j + 1)}
              className="btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        {isReviewingDoneStep ? (
          <div className="mt-4 rounded-2xl border border-green/25 bg-green-pale px-6 py-4 text-sm text-gray shadow-td-card">
            <p className="font-medium text-green">This step is complete</p>
            {recordForView?.completedAt ? (
              <p className="mt-1 text-gray">
                Completed {formatStepCompletedAt(recordForView.completedAt) ?? recordForView.completedAt}
              </p>
            ) : (
              <p className="mt-1 text-gray">You can review earlier steps or continue forward.</p>
            )}
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "kyb_verification" ? (
          <div className="mt-4 space-y-4 text-sm text-gray">
            <p>
              Enter your legal business details and Colorado Marijuana Enforcement Division (MED) credentials. Platform
              ops may request supporting documents separately.
            </p>
            <MetrcComplianceCallout variant="kyb" />
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">Legal name</span>
                <input
                  value={kybForm.legalName}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, legalName: e.target.value } : f))}
                  className="input-field mt-1"
                  autoComplete="organization"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">DBA (optional)</span>
                <input
                  value={kybForm.dbaName}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, dbaName: e.target.value } : f))}
                  className="input-field mt-1"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">Street address</span>
                <input
                  value={kybForm.addressLine1}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, addressLine1: e.target.value } : f))}
                  className="input-field mt-1"
                  autoComplete="street-address"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">Address line 2 (optional)</span>
                <input
                  value={kybForm.addressLine2}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, addressLine2: e.target.value } : f))}
                  className="input-field mt-1"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-light">City</span>
                <input
                  value={kybForm.city}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, city: e.target.value } : f))}
                  className="input-field mt-1"
                  autoComplete="address-level2"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-light">State</span>
                <input
                  value={kybForm.state}
                  onChange={(e) =>
                    setKybForm((f) =>
                      f ? { ...f, state: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) } : f,
                    )
                  }
                  className="input-field mt-1 font-mono"
                  maxLength={2}
                  autoComplete="address-level1"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-light">ZIP</span>
                <input
                  value={kybForm.zip}
                  onChange={(e) =>
                    setKybForm((f) => (f ? { ...f, zip: e.target.value.replace(/\D/g, "").slice(0, 5) } : f))
                  }
                  className="input-field mt-1 font-mono"
                  maxLength={5}
                  inputMode="numeric"
                  autoComplete="postal-code"
                />
              </label>
              <label className="block text-xs">
                <span className="text-gray-light">Phone</span>
                <input
                  value={kybForm.phone}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, phone: e.target.value } : f))}
                  className="input-field mt-1"
                  autoComplete="tel"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">Business email</span>
                <input
                  value={kybForm.email}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, email: e.target.value } : f))}
                  type="email"
                  className="input-field mt-1"
                  autoComplete="email"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">MED license number</span>
                <input
                  value={kybForm.medLicenseNumber}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, medLicenseNumber: e.target.value } : f))}
                  className="input-field mt-1 font-mono"
                />
              </label>
              <label className="block text-xs sm:col-span-2">
                <span className="text-gray-light">MED license expires</span>
                <input
                  value={kybForm.medLicenseExpiresAt}
                  onChange={(e) => setKybForm((f) => (f ? { ...f, medLicenseExpiresAt: e.target.value } : f))}
                  type="date"
                  className="input-field mt-1 font-mono"
                />
              </label>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-dark">
                  <input
                    type="checkbox"
                    checked={kybForm.hasDeliveryPermit}
                    onChange={(e) =>
                      setKybForm((f) => (f ? { ...f, hasDeliveryPermit: e.target.checked } : f))
                    }
                    className="rounded border-border"
                  />
                  Has Colorado cannabis delivery permit
                </label>
              </div>
              {kybForm.hasDeliveryPermit ? (
                <>
                  <label className="block text-xs sm:col-span-2">
                    <span className="text-gray-light">Delivery permit number</span>
                    <input
                      value={kybForm.deliveryPermitNumber}
                      onChange={(e) => setKybForm((f) => (f ? { ...f, deliveryPermitNumber: e.target.value } : f))}
                      className="input-field mt-1 font-mono"
                    />
                  </label>
                  <label className="block text-xs sm:col-span-2">
                    <span className="text-gray-light">Delivery permit expires</span>
                    <input
                      value={kybForm.deliveryPermitExpiresAt}
                      onChange={(e) =>
                        setKybForm((f) => (f ? { ...f, deliveryPermitExpiresAt: e.target.value } : f))
                      }
                      type="date"
                      className="input-field mt-1 font-mono"
                    />
                  </label>
                </>
              ) : null}
            </div>
            <button
              type="button"
              disabled={busy}
              className="btn-primary disabled:opacity-50"
              onClick={() => void onKybSaveAndContinue()}
            >
              Save and continue
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "bank_account_linking" ? (
          <div className="mt-4 space-y-4 text-sm text-gray">
            <p>
              Link a checking account with Plaid. The API stores Plaid tokens encrypted and, when Straddle is
              configured, creates a settlement paykey for payouts. Use a phone in E.164 format on your business profile
              if Straddle requires it.
            </p>
            {plaidTokenErr ? (
              <p className="text-sm text-red">{plaidTokenErr}</p>
            ) : plaidTokenLoading ? (
              <p className="text-xs text-gray">Preparing Plaid…</p>
            ) : plaidLinkToken ? (
              <div className="space-y-3">
                <TransporterPlaidLinkButton
                  linkToken={plaidLinkToken}
                  disabled={busy || plaidExchangeBusy}
                  label={plaidExchangeBusy ? "Saving…" : "Link bank with Plaid"}
                  className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                  onPlaidSuccess={(publicToken, metadata) => {
                    void onBankPlaidSuccess(publicToken, metadata);
                  }}
                />
                <p className="text-xs text-gray">
                  After Plaid succeeds, this step is marked complete automatically.
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray">Could not load Plaid. Check API Plaid configuration.</p>
            )}
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "fee_schedule_agreement" ? (
          <div className="mt-4 space-y-3 text-sm text-gray">
            <p>
              Platform and per-delivery fees are defined in your agreement with The Drop and any dispensary-specific
              delivery contracts. Continuing confirms you accept the fee schedule currently on file for this transporter.
            </p>
            <ul className="list-inside list-disc space-y-1 text-gray">
              <li>Transporter delivery fee and platform fee rates applied to settled batches</li>
              <li>Subject to future contract amendments (you will be notified through your admin contacts)</li>
            </ul>
            <button
              type="button"
              disabled={busy}
              className="btn-primary disabled:opacity-50"
              onClick={() => void onComplete("fee_schedule_agreement")}
            >
              Accept fee schedule
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "vehicle_registration" ? (
          <div className="mt-4 space-y-3 text-sm text-gray">
            <p>
              Confirm every active delivery vehicle is listed in this system with current registration, insurance, and
              Colorado MED checklist fields (alarm, locked opaque storage, video). Add or update vehicles anytime under{" "}
              <span className="font-medium text-dark">Fleet</span>.
            </p>
            <MetrcComplianceCallout variant="fleet" />
            <button
              type="button"
              disabled={busy}
              className="btn-primary disabled:opacity-50"
              onClick={() => void onComplete("vehicle_registration", { attested: true })}
            >
              Mark vehicles registered
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "driver_registration" ? (
          <div className="mt-4 space-y-3 text-sm text-gray">
            <p>
              Confirm every driver who may operate for you is in the roster with valid MED occupational license and
              Colorado driver&apos;s license expirations on file. Invite and manage users under{" "}
              <span className="font-medium text-dark">Account → Team</span>.
            </p>
            <MetrcComplianceCallout variant="driver" />
            <button
              type="button"
              disabled={busy}
              className="btn-primary disabled:opacity-50"
              onClick={() => void onComplete("driver_registration", { attested: true })}
            >
              Mark drivers registered
            </button>
          </div>
        ) : null}

        {!isReviewingDoneStep && step === "completed" ? (
          <div className="mt-4 space-y-3 text-sm text-gray">
            <p>
              You have completed business verification, banking, fee acceptance, and fleet attestations. Submitting
              marks this transporter as ready for dispatch operations; MED, permit, vehicle, and driver details remain
              editable under Account and Fleet.
            </p>
            <button
              type="button"
              disabled={busy}
              className="btn-primary disabled:opacity-50"
              onClick={() => void onComplete("completed")}
            >
              Complete onboarding
            </button>
          </div>
        ) : null}

        {actionError ? <p className="mt-2 text-sm text-red">{actionError}</p> : null}
      </section>

      <div className="border-t border-border pt-4">
        <button
          type="button"
          disabled={viewIdx !== serverStepIdx}
          className="text-sm text-gray underline hover:text-dark disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40"
          onClick={() => setFailOpen((v) => !v)}
        >
          Report a problem with this step
        </button>
        {failOpen && viewIdx === serverStepIdx ? (
          <div className="mt-3 space-y-2">
            <textarea
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              className="input-field min-h-[88px] w-full py-2"
              placeholder="What went wrong?"
            />
            <button
              type="button"
              disabled={busy || !failReason.trim()}
              className="btn-secondary px-3 py-2 text-sm disabled:opacity-50"
              onClick={() => void onFail()}
            >
              Submit issue
            </button>
          </div>
        ) : null}
      </div>

      <p className="text-xs text-gray-light">
        Viewing step {viewIdx + 1} of {TRANSPORTER_ONBOARDING_FLOW.length}
        {serverStepIdx >= 0 ? (
          <span className="text-gray-400"> — current work is step {serverStepIdx + 1}</span>
        ) : null}
      </p>
    </div>
  );
}
